import { Heart, Target, ShieldCheck, Handshake, Linkedin, Compass, Users } from "lucide-react";
import { Link } from "react-router-dom";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import FinalCTA from "@/components/shared/FinalCTA";
import ProtectedImage from "@/components/shared/ProtectedImage";
import { SITE } from "@/lib/siteConfig";

const VALUES = [
  {
    icon: Heart,
    title: "Empathy First",
    text: "We listen before we advise. Every client's situation is unique and we take the time to truly understand it.",
  },
  {
    icon: Target,
    title: "Practical Impact",
    text: "We are not here to produce reports. We are here to create change that you can see and measure.",
  },
  {
    icon: ShieldCheck,
    title: "Integrity Always",
    text: "We say what we mean, do what we say and never overpromise.",
  },
  {
    icon: Handshake,
    title: "Long-Term Partnerships",
    text: "Our goal is not a transaction. It's a relationship built on trust, delivered over time.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative bg-[#F4F7FC] py-20 md:py-28 px-6 md:px-10 overflow-hidden">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <SectionEyebrow className="mb-6">About NextChapter4You</SectionEyebrow>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#1E2761] tracking-tight leading-tight">
            Where Technology Expertise<br />
            <span className="italic">Meets Human-First Thinking.</span>
          </h1>
          <p className="mt-8 text-base md:text-lg text-[#334155]/85 leading-relaxed max-w-3xl mx-auto">
            We exist because transformations succeed when people are led,
            supported and developed well, not because of the tools we use.
          </p>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="bg-white py-24 md:py-32 px-6 md:px-10" data-testid="our-story">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionEyebrow className="mb-4">Our Story</SectionEyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-[#1E2761] tracking-tight leading-tight">
              Why We Exist
            </h2>
            <div className="mt-6 relative aspect-[4/5] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/7176305/pexels-photo-7176305.jpeg"
                alt="A warm professional coaching conversation in a bright office"
                className="w-full h-full object-cover"
              />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 border-l-2 border-b-2 border-[#C9A84C]" />
            </div>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <p className="font-display text-2xl md:text-3xl text-[#1E2761] leading-snug tracking-tight">
              NextChapter4You was born from a simple belief: that lasting
              change starts with the person not the process. Careers only
              move forward when leaders and professionals are supported
              challenged and developed with genuine care.
            </p>
            <p className="text-base md:text-lg text-[#334155]/85 leading-relaxed">
              After years leading complex teams across Europe and working
              with professionals navigating career transitions, Ashutosh
              Pandey founded NextChapter4You.nl to bring executive coaching
              and expat career support together under one warm and
              human-first roof.
            </p>
            <p className="text-base md:text-lg text-[#334155]/85 leading-relaxed">
              Our name says it all. Every leader and every professional has
              a next chapter. We are here to help you write it well.
            </p>
          </div>
        </div>
      </section>

      {/* VISION + MISSION */}
      <section className="bg-[#F4F7FC] py-24 md:py-32 px-6 md:px-10" data-testid="vision-mission">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-[#1E2761] text-white p-10 md:p-14 relative overflow-hidden" data-testid="vision-box">
            <div className="absolute top-0 left-0 w-full h-1 bg-[#C9A84C]" />
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 border border-[#C9A84C] flex items-center justify-center text-[#C9A84C]">
                <Compass size={18} strokeWidth={1.5} />
              </div>
              <span className="text-xs uppercase tracking-[0.2em] text-[#C9A84C]">Our Vision</span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl text-white tracking-tight leading-snug mb-4">
              The Trusted Transformation Partner.
            </h3>
            <p className="text-white/80 leading-relaxed">
              To become a trusted growth partner, helping leaders and
              professionals navigate their next chapter successfully across
              executive coaching and career transition.
            </p>
          </div>

          <div className="bg-[#4A90D9] text-white p-10 md:p-14 relative overflow-hidden" data-testid="mission-box">
            <div className="absolute top-0 left-0 w-full h-1 bg-[#C9A84C]" />
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 border border-[#C9A84C] flex items-center justify-center text-[#C9A84C]">
                <Users size={18} strokeWidth={1.5} />
              </div>
              <span className="text-xs uppercase tracking-[0.2em] text-[#C9A84C]">Our Mission</span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl text-white tracking-tight leading-snug mb-4">
              Practical, People-Centric and Scalable.
            </h3>
            <p className="text-white/90 leading-relaxed">
              To deliver practical, people-centric coaching and career
              support, with integrity, empathy and measurable outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-white py-24 md:py-32 px-6 md:px-10" data-testid="values-section">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 max-w-2xl">
            <SectionEyebrow className="mb-4">Our Values</SectionEyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-[#1E2761] tracking-tight">
              What We Stand For
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="bg-white p-8 md:p-10" data-testid={`value-${i}`}>
                  <div className="flex items-baseline gap-3 mb-5">
                    <span className="font-display text-3xl text-[#C9A84C]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <Icon size={20} className="text-[#1E2761]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-xl text-[#1E2761] mb-3 tracking-tight">
                    {v.title}
                  </h3>
                  <p className="text-sm text-[#334155]/80 leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MEET ASHUTOSH */}
      <section className="bg-[#F4F7FC] py-24 md:py-32 px-6 md:px-10" data-testid="founder-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <ProtectedImage
                src={SITE.founderImage}
                alt="Ashutosh Pandey, Performance Coach and Founder, NextChapter4You"
                imgClassName="w-full h-full object-cover object-top"
              />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-r-2 border-b-2 border-[#C9A84C]" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <SectionEyebrow className="mb-4">Founder</SectionEyebrow>
            <h2 className="font-display text-4xl md:text-5xl text-[#1E2761] tracking-tight leading-tight">
              Meet Ashutosh Pandey.
            </h2>
            <div className="mt-2 text-base text-[#C9A84C] font-semibold tracking-wide">
              {SITE.founderTitle}
            </div>

            <div className="mt-8 space-y-5 text-base md:text-lg text-[#334155]/85 leading-relaxed">
              <p>
                Ashutosh brings over two decades of experience leading teams
                and mentoring senior professionals across European and
                international organisations, combined with deep expertise in
                executive coaching and career development.
              </p>
              <p>
                Having worked across sectors including banking, logistics, and
                public sector engagements, he understands the pressures senior
                leaders face and the real human cost when change is not
                managed with care.
              </p>
              <p>
                He founded NextChapter4You.nl to offer something rare: a
                coaching and career partner who has walked the executive road
                and puts the person at the centre of every conversation.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={SITE.linkedinBrand}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-linkedin"
                className="inline-flex items-center gap-3 bg-[#1E2761] text-white px-6 py-3 font-medium tracking-wide hover:bg-[#161d4b] transition-colors"
              >
                <Linkedin size={16} />
                Connect on LinkedIn
              </a>
              <Link
                to="/contact"
                data-testid="founder-contact"
                className="inline-flex items-center gap-3 border border-[#1E2761] text-[#1E2761] px-6 py-3 font-medium tracking-wide hover:bg-white transition-colors"
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
